"use client";

import { useEffect, useRef, useState } from "react";

const calm = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Looping festival clip (ping-pong encoded, so the loop has no seam). Plays only while on screen; reduced motion keeps the poster. */
export function FestVideo({ src, poster }: { src: string; poster: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current!;
    if (calm()) return;
    v.muted = true; // React doesn't reliably render the attribute; autoplay needs it
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) v.play().catch(() => {}); else v.pause(); });
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return <video ref={ref} className="fx-video" src={src} poster={poster} muted loop playsInline preload="metadata" aria-hidden />;
}

const COLORS = ["#ff7a00", "#ffd166", "#c084fc", "#f472b6", "#fff3e2", "#a78bfa"];

/** Firework bursts at random spots while the layer is on screen (DOM sparks, transform/opacity only). */
export function Fireworks({ every = 900, className = "" }: { every?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = ref.current!;
    if (calm()) return;
    let visible = false;
    const burst = () => {
      if (!visible || document.hidden) return;
      const b = document.createElement("span");
      b.className = "fw";
      b.style.left = `${8 + Math.random() * 84}%`;
      b.style.top = `${8 + Math.random() * 42}%`;
      b.style.setProperty("--c", COLORS[(Math.random() * COLORS.length) | 0]);
      const n = 14 + ((Math.random() * 6) | 0), r = 46 + Math.random() * 64;
      for (let k = 0; k < n; k++) {
        const i = document.createElement("i");
        i.style.setProperty("--a", `${(k * 360) / n}deg`);
        i.style.setProperty("--r", `${r * (0.8 + Math.random() * 0.4)}px`);
        b.appendChild(i);
      }
      box.appendChild(b);
      setTimeout(() => b.remove(), 1700);
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(box);
    const t = setInterval(burst, every);
    return () => { clearInterval(t); io.disconnect(); };
  }, [every]);
  return <div ref={ref} className={`fw-layer ${className}`} aria-hidden />;
}

/** Lantern-tile countdown; a digit that changes drops in (remount via key). "--" until mounted, so no clock mismatch. */
export function FestCountdown({ to, labels, ended }: { to: number; labels: [string, string, string, string]; ended: string }) {
  const [now, setNow] = useState(0);
  useEffect(() => {
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (now && now >= to) return <span className="fx-ended">{ended}</span>;
  const s = Math.max(0, Math.floor((to - now) / 1000));
  const parts = now ? [s / 86400, (s % 86400) / 3600, (s % 3600) / 60, s % 60].map((n) => String(Math.floor(n)).padStart(2, "0")) : ["--", "--", "--", "--"];
  return (
    <div className="fx-tiles">
      {parts.map((v, k) => (
        <div key={k} className="fx-tile">
          <span key={v} className="fx-digit tnum">{v}</span>
          <small>{labels[k]}</small>
        </div>
      ))}
    </div>
  );
}

/** Counts up to n once it scrolls into view. */
export function CountUp({ n, className = "" }: { n: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [v, setV] = useState(n);
  useEffect(() => {
    if (calm() || n <= 0) return;
    let raf = 0;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const step = (t: number) => {
        const p = Math.min(1, (t - t0) / 1300);
        setV(Math.round(n * (1 - (1 - p) ** 3)));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      setV(0);
      raf = requestAnimationFrame(step);
    });
    io.observe(ref.current!);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [n]);
  return <span ref={ref} className={`tnum ${className}`}>{v.toLocaleString("en-US")}</span>;
}
